import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import HolographicBg from "@/components/HolographicBg";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function EventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/events/${id}`);
        setEvent(data);
      } catch (err) {
        toast.error("Event not found");
      }
    };
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [id]);

  const purchase = () => {
    if (!user) { navigate(`/login?redirect=/event/${id}`); return; }
    // Influencer flow: comp tier = free, discount tier = paid w/ 25% off — both bypass guest list
    if (user.role === "influencer") {
      doCheckout(null);
      return;
    }
    navigate(`/guest-list/${id}`);
  };

  const doCheckout = async (guestListId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/ticket", {
        event_id: id,
        origin_url: window.location.origin,
        guest_list_id: guestListId,
      });
      if (data.free) {
        toast.success("Comp ticket reserved");
        navigate("/access-granted?type=ticket&free=1");
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (!event) return (
    <div className="relative min-h-screen"><HolographicBg /><TopBar />
      <p className="relative z-20 text-center mt-20 font-mono text-white/60">Loading event...</p>
    </div>
  );

  const pctSold = Math.round((event.sold / event.capacity) * 100);
  const closing = event.remaining > 0 && event.remaining <= Math.max(3, event.capacity * 0.15);
  const sold = event.remaining === 0;

  return (
    <div className="relative min-h-screen">
      <HolographicBg />
      <TopBar />
      <main className="relative z-20 flex items-center justify-center px-6 py-12">
        <div className="glass-strong rounded-sm p-8 sm:p-12 w-full max-w-3xl" data-testid="event-page">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80">— tonight's drop —</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase mt-2 neon-cyan" data-testid="event-name">{event.name}</h1>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8 font-mono text-xs uppercase tracking-widest">
            <div><div className="text-white/40">Date</div><div className="text-white mt-1">{event.date}</div></div>
            <div><div className="text-white/40">Venue</div><div className="text-white mt-1">{event.hidden_location ? "Disclosed 24h prior" : event.venue}</div></div>
            <div><div className="text-white/40">Tier</div><div className="text-cyan-400 mt-1">Restricted</div></div>
          </div>

          {/* Price panel — reservation online + cash at door if any */}
          <div className="mt-6 glass rounded-sm p-5 border border-cyan-400/20" data-testid="event-price-panel">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400/80">Reserve online</div>
                <div className="font-display text-3xl font-black text-white mt-1" data-testid="event-price-reserve">${Number(event.price).toFixed(2)}</div>
              </div>
              {event.door_price > 0 && (
                <>
                  <div className="font-mono text-white/30 text-2xl">+</div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/80">Cash at door</div>
                    <div className="font-display text-3xl font-black text-fuchsia-400 mt-1" data-testid="event-price-door">${Number(event.door_price).toFixed(2)}</div>
                  </div>
                  <div className="font-mono text-white/30 text-2xl">=</div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Total</div>
                    <div className="font-display text-3xl font-black text-white/80 mt-1" data-testid="event-price-total">${(Number(event.price) + Number(event.door_price)).toFixed(2)}</div>
                  </div>
                </>
              )}
            </div>
            {event.door_price > 0 && (
              <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-fuchsia-400/80" data-testid="event-price-note">
                ◆ Pay only ${Number(event.price).toFixed(2)} to secure your spot · ${Number(event.door_price).toFixed(2)} cash at the door
                <span className="block mt-2 text-rose-400 font-bold">◆ NO CASH · NO ENTRY</span>
              </p>
            )}
          </div>

          {event.description && <p className="mt-8 font-mono text-sm text-white/70 leading-relaxed">{event.description}</p>}

          {/* Capacity */}
          <div className="mt-10">
            <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest">
              <span className="text-white/60">Live capacity</span>
              <span className="text-white" data-testid="event-capacity-counter">{event.sold} / {event.capacity}</span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-400 via-white to-fuchsia-400 transition-all" style={{ width: `${pctSold}%` }} />
            </div>
            <div className="flex gap-3 mt-4 text-[10px] uppercase tracking-[0.3em] font-bold">
              {sold && <span className="text-rose-400 flex items-center gap-2"><span className="urgency-dot inline-block w-1.5 h-1.5 rounded-full bg-rose-400" />Sold out</span>}
              {!sold && closing && <span className="text-rose-400 flex items-center gap-2"><span className="urgency-dot inline-block w-1.5 h-1.5 rounded-full bg-rose-400" />Closing soon</span>}
              {!sold && <span className="text-white/60">Limited capacity</span>}
              <span className="text-cyan-300">Access restricted</span>
            </div>
          </div>

          {user?.role === "user" && user?.membership_active && (
            <div className="mt-6 text-xs font-mono uppercase tracking-widest text-cyan-400">◆ Inner Circle 15% discount applies</div>
          )}
          {user?.role === "influencer" && user?.influencer_tier !== "discount" && (
            <div className="mt-6 text-xs font-mono uppercase tracking-widest text-fuchsia-400">◆ VIP comp entry — free</div>
          )}
          {user?.role === "influencer" && user?.influencer_tier === "discount" && (
            <div className="mt-6 text-xs font-mono uppercase tracking-widest text-fuchsia-400">◆ VIP 25% discount applies at checkout</div>
          )}

          {sold ? (
            <WaitlistForm eventId={id} />
          ) : (
            <>
              <button
                onClick={purchase}
                disabled={loading || sold}
                data-testid="event-secure-entry-btn"
                className="gate mt-10 w-full py-4 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-sm rounded-sm hover:scale-[1.01] transition disabled:opacity-40"
              >
                {loading ? "..." : event.door_price > 0 ? `Reserve — $${Number(event.price).toFixed(2)}` : "Secure Entry"}
              </button>
              {!user && (
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-4">
                  <Link to={`/login?redirect=/event/${id}`} className="text-cyan-400 hover:underline">Sign in</Link> required to proceed
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function WaitlistForm({ eventId }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [state, setState] = useState("form"); // form | joined
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${eventId}/waitlist`, { email: email.trim(), full_name: fullName.trim() });
      setPosition(data.position);
      setState("joined");
      toast.success(data.already_on_list ? "You are already on the waitlist." : `You are #${data.position} on the waitlist.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join waitlist");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-10 glass-strong rounded-sm p-6 border border-fuchsia-400/25" data-testid="event-waitlist-panel">
      <div className="text-center">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-rose-400/90 mb-2">— Sold Out —</div>
        <h2 className="font-display text-2xl font-black uppercase text-white">The room is full.</h2>
        <p className="font-mono text-[11px] uppercase tracking-widest text-white/60 mt-3">
          Leave your name. If a spot opens, you&#39;ll be the first to know.
        </p>
      </div>

      {state === "joined" ? (
        <div className="mt-6 text-center" data-testid="event-waitlist-joined">
          <div className="glass rounded-sm p-6 border border-fuchsia-400/25">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-fuchsia-400/80">— on the list —</div>
            <div className="font-display text-3xl font-black uppercase text-white mt-2">
              You&#39;re {position ? `#${position}` : "in"}.
            </div>
            <p className="font-mono text-[11px] text-white/60 mt-4">
              Watch <span className="text-cyan-400">{email}</span> — if a spot opens, we email everyone on the list at once. First to reserve wins.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            required minLength={2} maxLength={120}
            value={fullName} onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name"
            data-testid="event-waitlist-name"
            className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60"
          />
          <input
            required type="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            data-testid="event-waitlist-email"
            className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60"
          />
          <button
            type="submit" disabled={loading}
            data-testid="event-waitlist-submit"
            className="gate w-full py-3 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-sm rounded-sm hover:scale-[1.02] transition disabled:opacity-50"
          >
            {loading ? "..." : "Join Waitlist"}
          </button>
          <p className="font-mono text-[10px] text-white/40 text-center">
            No charge · You only pay if a spot opens and you claim it
          </p>
        </form>
      )}
    </div>
  );
}

